import { createTheme, alpha } from '@mui/material/styles';

const font = '"Manrope", "Rubik", "Segoe UI", sans-serif';

const theme = createTheme({
  spacing: 8,
  shape: {
    borderRadius: 14,
  },
  transitions: {
    duration: {
      shortest: 100,
      shorter: 120,
      short: 150,
      standard: 180,
      complex: 220,
      enteringScreen: 160,
      leavingScreen: 120,
    },
  },
  palette: {
    mode: 'light',
    primary: {
      main: '#0F766E',
      light: '#14B8A6',
      dark: '#0D5C56',
      contrastText: '#fff',
    },
    secondary: {
      main: '#C2410C',
      light: '#EA580C',
      dark: '#9A3412',
    },
    background: {
      default: '#EEF2F4',
      paper: '#FFFFFF',
    },
    text: {
      primary: '#1C2B33',
      secondary: '#5B6B75',
    },
    divider: alpha('#1C2B33', 0.08),
    success: { main: '#15803D' },
    warning: { main: '#B45309' },
    error: { main: '#B91C1C' },
    info: { main: '#0369A1' },
  },
  typography: {
    fontFamily: font,
    fontSize: 14,
    h4: { fontFamily: font, fontWeight: 700, fontSize: '1.5rem', letterSpacing: '-0.02em', lineHeight: 1.25 },
    h5: { fontFamily: font, fontWeight: 700, fontSize: '1.25rem', letterSpacing: '-0.02em', lineHeight: 1.3 },
    h6: { fontFamily: font, fontWeight: 600, fontSize: '1.05rem', letterSpacing: '-0.01em', lineHeight: 1.35 },
    subtitle1: { fontWeight: 600, fontSize: '0.95rem', lineHeight: 1.35 },
    subtitle2: { fontWeight: 600, fontSize: '0.85rem', lineHeight: 1.35 },
    body1: { fontSize: '0.9rem', lineHeight: 1.45 },
    body2: { fontSize: '0.82rem', lineHeight: 1.4 },
    button: { textTransform: 'none', fontWeight: 600, letterSpacing: 0 },
    caption: { fontSize: '0.75rem', lineHeight: 1.35 },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        html: {
          maxWidth: '100%',
          height: '100%',
        },
        body: {
          fontFamily: font,
          maxWidth: '100%',
          minHeight: '100%',
          overflowX: 'clip',
          backgroundImage:
            'radial-gradient(ellipse 90% 60% at 0% -10%, rgba(20, 184, 166, 0.12), transparent 50%),' +
            'radial-gradient(ellipse 70% 50% at 100% 0%, rgba(3, 105, 161, 0.08), transparent 45%)',
        },
        '#root': {
          maxWidth: '100%',
          minWidth: 0,
          minHeight: '100%',
        },
        code: {
          fontFamily: '"IBM Plex Mono", "SF Mono", Menlo, Consolas, monospace',
          fontSize: '0.85em',
          backgroundColor: alpha('#0F766E', 0.08),
          padding: '1px 6px',
          borderRadius: 6,
        },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: 12,
          paddingInline: 14,
          paddingBlock: 7,
          minHeight: 36,
        },
        sizeSmall: {
          borderRadius: 10,
          paddingInline: 10,
          paddingBlock: 4,
          minHeight: 30,
        },
        contained: {
          boxShadow: '0 1px 2px rgba(28, 43, 51, 0.08)',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(15, 118, 110, 0.22)',
          },
        },
        outlined: {
          borderColor: alpha('#1C2B33', 0.14),
          backgroundColor: alpha('#fff', 0.55),
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: { borderRadius: 12 },
      },
    },
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          border: `1px solid ${alpha('#1C2B33', 0.07)}`,
          boxShadow: '0 1px 2px rgba(28, 43, 51, 0.04), 0 8px 24px rgba(28, 43, 51, 0.04)',
        },
        outlined: {
          boxShadow: 'none',
          border: `1px solid ${alpha('#1C2B33', 0.1)}`,
        },
      },
    },
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          borderRadius: 16,
          border: `1px solid ${alpha('#1C2B33', 0.07)}`,
          boxShadow: '0 1px 2px rgba(28, 43, 51, 0.04), 0 8px 24px rgba(28, 43, 51, 0.04)',
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: 16,
          '&:last-child': { paddingBottom: 16 },
        },
      },
    },
    MuiAppBar: {
      defaultProps: { elevation: 0, color: 'transparent' },
      styleOverrides: {
        root: {
          backgroundColor: alpha('#F8FBFC', 0.82),
          backdropFilter: 'blur(12px)',
          borderBottom: `1px solid ${alpha('#1C2B33', 0.08)}`,
          color: '#1C2B33',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRight: `1px solid ${alpha('#1C2B33', 0.08)}`,
          backgroundColor: '#F7FBFC',
          backgroundImage: 'none',
          boxShadow: 'none',
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          marginInline: 8,
          marginBlock: 2,
          paddingBlock: 8,
          '&.Mui-selected': {
            backgroundColor: alpha('#0F766E', 0.12),
            color: '#0D5C56',
            '& .MuiListItemIcon-root': { color: '#0F766E' },
            '&:hover': { backgroundColor: alpha('#0F766E', 0.16) },
          },
        },
      },
    },
    MuiTextField: {
      defaultProps: { size: 'small', margin: 'none' },
    },
    MuiFormControl: {
      defaultProps: { size: 'small' },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          backgroundColor: alpha('#fff', 0.7),
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: alpha('#0F766E', 0.4),
          },
        },
        notchedOutline: {
          borderColor: alpha('#1C2B33', 0.12),
        },
      },
    },
    MuiSelect: {
      defaultProps: { size: 'small' },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 999,
          fontWeight: 600,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: '8px 10px',
          borderColor: alpha('#1C2B33', 0.06),
        },
        head: {
          fontWeight: 700,
          color: '#5B6B75',
          backgroundColor: alpha('#0F766E', 0.04),
          fontSize: '0.75rem',
          letterSpacing: '0.02em',
          textTransform: 'uppercase',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: alpha('#0F766E', 0.03),
          },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          border: `1px solid ${alpha('#1C2B33', 0.06)}`,
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          borderRadius: '14px !important',
          border: `1px solid ${alpha('#1C2B33', 0.07)}`,
          boxShadow: 'none',
          '&:before': { display: 'none' },
          overflow: 'hidden',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 18,
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          minHeight: '52px !important',
        },
      },
    },
    MuiContainer: {
      defaultProps: {
        maxWidth: false,
      },
    },
    MuiBottomNavigation: {
      styleOverrides: {
        root: {
          height: 64,
          borderTop: `1px solid ${alpha('#1C2B33', 0.08)}`,
          backgroundColor: alpha('#F8FBFC', 0.95),
          backdropFilter: 'blur(12px)',
        },
      },
    },
    MuiBottomNavigationAction: {
      styleOverrides: {
        root: {
          minWidth: 0,
          paddingTop: 8,
          color: '#5B6B75',
          '&.Mui-selected': { color: '#0F766E' },
        },
        label: {
          fontSize: '0.68rem',
          '&.Mui-selected': { fontSize: '0.7rem' },
        },
      },
    },
  },
});

export default theme;
